import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useApi } from 'model/Api';
import { WorkResource } from 'model/WorkResource';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import H2 from 'component/part/H2';

type PageWorkResourceParams = {
  [key: string]: {
    types: WorkResource['type'][];
    typeLabel: string;
  };
};

const pageWorkResourceParams: PageWorkResourceParams = {
  machinery: { types: ['machinery'], typeLabel: '重機' },
  att: { types: ['att'], typeLabel: 'ATT' },
  car: { types: ['car'], typeLabel: '車両' },
  ['industrial-waste']: { types: ['industrial_waste'], typeLabel: '産廃' },
  general: { types: ['person', 'tool', 'car_related', 'other'], typeLabel: '工具' },
};

const pageWorkResourceLabels: { [type in WorkResource['name']]: string } = {
  machinery: '重機',
  att: 'ATT',
  car: '車両',
  industrial_waste: '産廃',
  person: '職種',
  tool: '工具',
  car_related: '車両関連',
  other: 'その他',
};

type PageWorkResources<TS extends WorkResource['type'][]> = {
  [type in TS[number]]: WorkResource[];
};

const makeInitialPageWorkResources = <TS extends WorkResource['type'][]>(types: TS): PageWorkResources<TS> =>
  types.reduce((list, type) => ({ ...list, [type]: [] }), {}) as PageWorkResources<TS>;

const valueTypes: { value: WorkResource['valueType'], label: string }[] = [
  { value: 'count', label: '数値選択（1〜10）' },
  { value: 'quantity', label: '数値入力' },
  { value: 'volume', label: '数量入力（小数可）' },
  { value: 'text', label: 'テキスト入力' },
  { value: 'free_text', label: '自由記述' },
];

const style = {
  form: {
    ...generalStyle.form,
    gap: '0',
    width: '80rem',
    maxWidth: '100%',
  },
  formRow: (type: WorkResource['type'], valueType: WorkResource['valueType']) => ({
    ...generalStyle.formRow,
    display: 'grid',
    gridTemplateColumns: (() => {
      switch (type) {
        case 'person':
          return  '7fr 1fr 2fr';
        case 'machinery':
        case 'att':
        case 'car':
          return '8fr 2fr';
        default:
          return ['text', 'free_text'].includes(valueType)
            ? '8fr 2fr'
            : '5fr 2fr 1fr 2fr';
      }
    })(),
  }),
};

const WorkResourceList: React.FC = () => {
  const api = useApi();
  const location = useLocation();
  const urlParams = location.pathname.match(/^\/(.*?)\//) || [];
  const typeCode = urlParams[1] || 'general';
  const { types } = pageWorkResourceParams[typeCode] || pageWorkResourceParams.general;
  const [workResources, setWorkResources] = useState<PageWorkResources<typeof types>>(makeInitialPageWorkResources(types));

  useEffect(() => {
    const initialWorkResources = makeInitialPageWorkResources(types);
    setWorkResources(initialWorkResources);
    (async () => {
      const workResourcesResponse = await api.workResource.getsByOwn({ types });
      workResourcesResponse.forEach((item) => {
        initialWorkResources[item.type].push({ ...item });
      });
      setWorkResources({ ...initialWorkResources });
    })();
  }, [typeCode]);

  return <Layout>
    <BackLink link='/' label='トップ'></BackLink>
    <div css={generalStyle.form}>
      {types.map((type) => (
        <div css={style.form} key={type}>
          <H2>{pageWorkResourceLabels[type]}一覧</H2>
          {(workResources[type] || []).map((workResource, typeIndex) => (
            <div css={style.formRow(type, workResource.valueType)} key={`${type}.${typeIndex}`}>
              <FormContent label='名称'>
                <div css={generalStyle.item}>{workResource.name}</div>
              </FormContent>
              {(type !== 'person' && !['text', 'free_text'].includes(workResource.valueType)) && (
                <FormContent label='単価'>
                  <div css={generalStyle.item}>&yen;{workResource.unitPrice?.toLocaleString()}</div>
                </FormContent>
              )}
              {(!['machinery', 'att', 'car'].includes(type) && !['text', 'free_text'].includes(workResource.valueType)) && (
                <FormContent label='単位'>
                  <div css={generalStyle.item}>{workResource.unitName}</div>
                </FormContent>
              )}
              {!['machinery', 'att', 'car'].includes(type) && (
                <FormContent label='入力タイプ'>
                  <div css={generalStyle.item}>
                    {valueTypes.find(({ value }) => value === workResource.valueType)?.label}
                  </div>
                </FormContent>
              )}
            </div>
          ))}
        </div>
      ))}
      <div css={generalStyle.formRow}>
        <NavLink to={`/${typeCode}/edit`} css={generalStyle.button()}>編集</NavLink>
      </div>
    </div>
  </Layout>;
};

export default WorkResourceList;
